import {
  MobileActiveFilters,
  MobileFilterOptionButton,
} from "./MobileFilters.styled";
import { MessageDescriptor } from "@lingui/core";
import { ReactNode, useCallback, useMemo, useState } from "react";
import { useLingui } from "@lingui/react";
import { ArrowRightIcon } from "src/icons";
import { Text } from "@otrium/atoms";
import { FilterType } from "./MobileFilters";
import { MobileRangeFilter } from "src/molecules/MobileFilter/MobileRangeFilter";
import { PLPType } from "src/types/PLPType.d";
import {
  useClearRefinements,
  useCurrentRefinements,
  useRange,
} from "react-instantsearch-core-v7";
import { Price } from "src/atoms/Price";
import { useRemoveFilters } from "src/hooks/use-handle-search-params";
import { theme } from "src/theme";

type MobileFilterPriceOptionProps = {
  title: MessageDescriptor;
  attribute: string;
  icon: ReactNode;
  type: FilterType;
  isMultiColumn: boolean;
  categoryPageId: string;
  gender: string;
  pageContentType: PLPType;
  handleScrollToTop: () => void;
};

function MobileFilterPriceOption({
  title,
  attribute,
  icon,
  isMultiColumn,
  handleScrollToTop,
}: MobileFilterPriceOptionProps): JSX.Element {
  const { _ } = useLingui();
  const [isOpen, setOpen] = useState(false);
  const { items: currentRefinements } = useCurrentRefinements({
    includedAttributes: [attribute],
  });
  const { range, canRefine } = useRange({ attribute });
  const { refine: clear } = useClearRefinements({
    includedAttributes: [attribute],
  });

  const priceRefinemnt = useMemo(() => {
    return currentRefinements.flatMap((item) => item.refinements);
  }, [currentRefinements]);

  const priceFilters = useMemo(() => {
    const values = priceRefinemnt.filter(
      (item) => item.attribute === attribute
    );

    return {
      min:
        Number(values.find((item) => item.operator === ">=")?.value) ||
        range.min,
      max:
        Number(values.find((item) => item.operator === "<=")?.value) ||
        range.max,
    };
  }, [attribute, priceRefinemnt, range.min, range.max]);

  const toggleOpen = useCallback(() => setOpen((prevState) => !prevState), []);

  const { removePriceFilters } = useRemoveFilters();

  const handleClear = useCallback(() => {
    clear();
    removePriceFilters();
  }, [clear, removePriceFilters]);

  return (
    <>
      <MobileFilterOptionButton onClick={toggleOpen} disabled={!canRefine}>
        {icon}
        <Text flexShrink="0" color={theme.colors.tone.black}>
          {_(title)}
        </Text>
        {priceRefinemnt.length > 0 ? (
          <MobileActiveFilters>
            <Price price={Number(priceFilters.min)} /> -{" "}
            <Price price={Number(priceFilters.max)} />
          </MobileActiveFilters>
        ) : null}
        <ArrowRightIcon />
      </MobileFilterOptionButton>

      <MobileRangeFilter
        title={title}
        attribute={attribute}
        isOpen={isOpen}
        toggleOpen={toggleOpen}
        isMultiColumn={isMultiColumn || false}
        onClear={handleClear}
        handleScrollToTop={handleScrollToTop}
      />
    </>
  );
}

export { MobileFilterPriceOption };
